// MCHD Room Scheduler - Expandable Panel Component
// Wrapper for smooth 200ms expand/collapse animation (2025 UX standard)
// Created: 08-10-2025 15:27:00

import { Box } from '@mantine/core';
import { ReactNode } from 'react';

/**
 * Props for ExpandablePanel component
 */
export interface ExpandablePanelProps {
  /** Whether panel is expanded */
  expanded: boolean;
  /** Panel content */
  children: ReactNode;
  /** Animation duration in milliseconds (default: 200ms) */
  duration?: number;
}

/**
 * ExpandablePanel - Smooth expand/collapse animation wrapper
 * 
 * Provides smooth 200ms animation for expanding/collapsing content panels
 * following 2025 UX standards. Uses CSS transitions for performance.
 * 
 * @example
 * <ExpandablePanel expanded={filterPanelOpen}>
 *   <FilterControls />
 * </ExpandablePanel>
 * 
 * @param expanded - Whether panel is currently expanded
 * @param children - Content to show/hide
 * @param duration - Animation duration in ms (default: 200)
 * @returns JSX element
 */
export function ExpandablePanel({
  expanded,
  children,
  duration = 200,
}: ExpandablePanelProps): JSX.Element {
  return (
    <Box
      style={{
        overflow: 'hidden',
        maxHeight: expanded ? '1000px' : '0',
        opacity: expanded ? 1 : 0,
        transition: `max-height ${duration}ms ease-out, opacity ${duration}ms ease-out`,
      }}
    >
      {children}
    </Box>
  );
}

export default ExpandablePanel;

